import React, { useState } from 'react';
import { Submission, Participant, Task, Project } from '../types';
import DashboardStatsRow from './DashboardStatsRow';
import TaskProgress from './TaskProgress';
import ParticipantActivity from './ParticipantActivity';
import ParticipantProgressTable from './ParticipantProgressTable';
import AnalyticsCharts from './AnalyticsCharts';
import SignificationDashboard from './SignificationDashboard';
import { ParticipantGroup } from '../types';
import { exportTasksToCSV, exportSubmissionsToCSV } from '../utils/exportUtils';
import { DownloadIcon, CheckSquareIcon, DatabaseIcon } from './icons';

interface DashboardViewProps {
  submissions: Submission[];
  tasks: Task[];
  participants: Participant[];
  participantsMap: Map<string, Participant>;
  setSelectedSubmission: (submission: Submission) => void;
  totalParticipantCount: number;
  participantGroups: ParticipantGroup[];
  currentProject?: Project | null;
}

type DashboardTab = 'analytics' | 'signification';

const DashboardView: React.FC<DashboardViewProps> = ({
  submissions,
  tasks,
  participants,
  participantsMap,
  setSelectedSubmission,
  totalParticipantCount,
  participantGroups,
  currentProject,
}) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('analytics');

  return (
    <div className="space-y-6 lg:space-y-8" data-tutorial-id="dashboard-view">
      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-white border border-app-border rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${
            activeTab === 'analytics'
              ? 'bg-action text-white'
              : 'text-text-muted hover:text-text-ink hover:bg-app-bg'
          }`}
        >
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('signification')}
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${
            activeTab === 'signification'
              ? 'bg-action text-white'
              : 'text-text-muted hover:text-text-ink hover:bg-app-bg'
          }`}
        >
          Signification
        </button>
      </div>

      {activeTab === 'analytics' ? (
        <>
          {/* Header Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-text-ink">Project Analytics</h2>
              <p className="text-sm text-text-muted">Overview of progression and data exports.</p>
            </div>
            <div className="flex flex-row items-center gap-2 w-auto">
              <button
                onClick={() => exportTasksToCSV(tasks, participantsMap, participantGroups)}
                className="flex items-center justify-center gap-1.5 p-2 sm:px-4 sm:py-2 bg-white border border-app-border text-text-ink text-sm font-medium rounded-lg hover:bg-app-bg active:scale-95 transition-colors"
                title="Export Tasks"
              >
                <div className="flex items-center gap-1">
                  <CheckSquareIcon className="w-4 h-4 opacity-70" />
                  <DownloadIcon className="w-4 h-4" />
                </div>
                <span className="hidden sm:inline text-sm">Tasks</span>
              </button>
              <button
                onClick={() => exportSubmissionsToCSV(submissions, tasks, participantsMap)}
                className="flex items-center justify-center gap-1.5 p-2 sm:px-4 sm:py-2 bg-action text-white text-sm font-medium rounded-lg hover:bg-action/90 active:scale-95 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-action"
                title="Export Submissions"
              >
                <div className="flex items-center gap-1">
                  <DatabaseIcon className="w-4 h-4 opacity-70" />
                  <DownloadIcon className="w-4 h-4" />
                </div>
                <span className="hidden sm:inline text-sm">Submissions</span>
              </button>
            </div>
          </div>

          {/* Top: Stats Row */}
          <DashboardStatsRow
            submissions={submissions}
            participants={participants}
            tasks={tasks}
          />

          {/* Analytics Charts */}
          <AnalyticsCharts
            submissions={submissions}
            tasks={tasks}
            participants={participants}
            participantGroups={participantGroups}
          />

          {/* Task Progress + Participant Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <TaskProgress
              tasks={tasks}
              submissions={submissions}
              participants={participants}
              totalParticipantCount={totalParticipantCount}
            />
            <ParticipantActivity
              submissions={submissions}
              participantsMap={participantsMap}
              onSubmissionSelect={setSelectedSubmission}
            />
          </div>

          {/* Participant Progress Table */}
          <ParticipantProgressTable
            participants={participants}
            submissions={submissions}
            tasks={tasks}
          />
        </>
      ) : (
        <SignificationDashboard
          submissions={submissions}
          tasks={tasks}
          participants={participants}
          participantsMap={participantsMap}
          currentProject={currentProject}
        />
      )}
    </div>
  );
};

export default DashboardView;
